// Trip settings modal (T27) — separate from ShareDialog because trip-scoped
// preferences aren't a sharing/membership concern. Holds the actor's own staging
// opt-in plus the trip-wide "packing together" toggle (T23 §2).

import type { CSSProperties, ReactNode } from "react";
import { Dialog, Select, Toggle, TRIP_HUES, tripHueColor, vars, type TripColor } from "@pack-it-up/ui-web";
import type { TemplateDisplay } from "@pack-it-up/core";

const hintStyle: CSSProperties = {
  fontSize: vars.font.sizeXs,
  color: vars.color.textMuted,
  marginTop: vars.space.xs,
};

const sectionStyle: CSSProperties = {
  marginTop: vars.space.md,
  paddingTop: vars.space.md,
  borderTop: `1px solid ${vars.color.border}`,
};

const sectionLabelStyle: CSSProperties = {
  fontSize: vars.font.sizeSm,
  fontWeight: vars.font.weightMedium,
  color: vars.color.text,
  marginBottom: vars.space.sm,
};

/** Trip-wide "check off for others" setting (T23 §2). */
export interface ProxyCompletionSetting {
  value: boolean;
  onChange: (value: boolean) => void;
  /** Only owners/editors may flip the setting; viewers see it disabled. */
  canEdit: boolean;
}

/** Trip-wide color-coding (sidebar dot, card stripe, countdown pill). */
export interface TripColorSetting {
  /** Unset = the UI falls back to a hue derived from the trip id. */
  value: TripColor | undefined;
  onChange: (value: TripColor) => void;
  /** Only owners/editors may change it; viewers see it disabled. */
  canEdit: boolean;
}

/** Per-member "Done last" sort, shared by the packing and to-do lists (issue #228). */
export interface CompletedToBottomSetting {
  value: boolean;
  onChange: (value: boolean) => void;
}

/**
 * How a row's source-template auto-tag renders for the signed-in member: a tag chip,
 * or a color highlight. Self-scoped like the staging opt-in (issue #228).
 */
export interface TemplateDisplaySetting {
  value: TemplateDisplay;
  onChange: (value: TemplateDisplay) => void;
}

export interface TripSettingsDialogProps {
  open: boolean;
  onClose: () => void;
  /** The signed-in member's own staging opt-in — self-service, any role. */
  stagingOptIn: boolean;
  onChangeStagingOptIn: (value: boolean) => void;
  proxyCompletion: ProxyCompletionSetting;
  tripColor: TripColorSetting;
  completedToBottom: CompletedToBottomSetting;
  templateDisplay: TemplateDisplaySetting;
  /**
   * Whether the trip-wide sections render at all (issue #228). An anonymous guest gets
   * the self-scoped settings only — it may configure its own view of the trip, never
   * the trip itself.
   */
  showTripWide: boolean;
}

export const TripSettingsDialog = ({
  open,
  onClose,
  stagingOptIn,
  onChangeStagingOptIn,
  proxyCompletion,
  tripColor,
  completedToBottom,
  templateDisplay,
  showTripWide,
}: TripSettingsDialogProps): ReactNode => (
  <Dialog open={open} onClose={onClose} title="Trip settings" compact>
    <Toggle checked={stagingOptIn} onChange={onChangeStagingOptIn} label="Show the staged step for me" />
    <div style={hintStyle}>
      Adds a "pulled out" step before packed: unstaged → staged → packed. Only affects your own checkbox.
    </div>

    <div style={sectionStyle}>
      <Toggle
        checked={completedToBottom.value}
        onChange={completedToBottom.onChange}
        label="Done last"
      />
      <div style={hintStyle}>
        Sort packed/done rows to the bottom of the packing and to-do lists.
      </div>
    </div>

    <div style={sectionStyle}>
      <Select
        label="Show template tags as"
        value={templateDisplay.value}
        onChange={(v) => templateDisplay.onChange(v as TemplateDisplay)}
        options={[
          { value: "tag", label: "Tag" },
          { value: "highlight", label: "Highlight" },
        ]}
      />
      <div style={hintStyle}>
        "Tag" shows the source template's name as a tag chip. "Highlight" instead shows a
        thin color bar down the row, in the template's color.
      </div>
    </div>

    {showTripWide && (
      <>
        <div style={sectionStyle}>
          <Toggle
            checked={proxyCompletion.value}
            onChange={proxyCompletion.onChange}
            disabled={!proxyCompletion.canEdit}
            label="Check off for others"
          />
          <div style={hintStyle}>
            Let members mark items packed or done on another member's behalf.
          </div>
        </div>

        <div style={sectionStyle}>
          <div style={sectionLabelStyle}>Trip color</div>
          <div style={{ display: "flex", gap: vars.space.sm, flexWrap: "wrap" }} role="group" aria-label="Trip color">
            {TRIP_HUES.map((hue) => (
              <button
                key={hue}
                type="button"
                aria-label={`Set trip color to ${hue}`}
                aria-pressed={tripColor.value === hue}
                disabled={!tripColor.canEdit}
                onClick={() => tripColor.onChange(hue)}
                style={{
                  width: "24px",
                  height: "24px",
                  borderRadius: vars.radius.full,
                  background: tripHueColor[hue],
                  border: `2px solid ${tripColor.value === hue ? vars.color.text : "transparent"}`,
                  outline: tripColor.value === hue ? `2px solid ${tripHueColor[hue]}` : "none",
                  outlineOffset: "1px",
                  cursor: tripColor.canEdit ? "pointer" : "not-allowed",
                  opacity: tripColor.canEdit ? 1 : 0.5,
                  padding: 0,
                }}
              />
            ))}
          </div>
          <div style={hintStyle}>Colors the trip's sidebar dot and home screen card.</div>
        </div>
      </>
    )}
  </Dialog>
);
